import Vue from 'vue';
import SparePartsCollection from './store/SparePartsCollection';
import store from './store/request';
import { bind, prop, compose, map, is, invoker, converge, equals, unless } from 'ramda';
import BX from 'BX';
import {
	adoptAct,
	adoptRepair,
	adoptPart,
	adoptIris,
	adoptDevice,
	adoptResponsible,
	adoptService,
	adoptActForm,
} from './adapters';
import { extend, getAbilityPromise } from '~/defineAbility';
import { Logistic } from './Logistic';
import { Conclusion } from './Conclusion';
import { RequestsListBySerialNumber } from '~/components/RequestsListBySerialNumber';
import { WarrantyModal, WarrantyModalToggle } from '~/legacy/request.new/WarrantyModal';

Vue.component('logistic', Logistic);
Vue.component('conclusion', Conclusion);
Vue.component('requests-list-by-serial-number', RequestsListBySerialNumber);
Vue.component('warranty-modal', WarrantyModal);
Vue.component('warranty-modal-toggle', WarrantyModalToggle);
const getElementById = bind(Document.prototype.getElementById, document);
const appendToBody = (element) => document.body.appendChild(element);

async function makeRepairs() {
	const mountElement = getElementById('repairs');

	if (!mountElement)
		return;

	const { ServiceRequestRepairList } = await import('./ServiceRequestRepairList');
	const list = new Vue(ServiceRequestRepairList);

	list.$mount(mountElement);
}

async function makeActs() {
	const mountElement = getElementById('acts');

	if (!mountElement)
		return;

	const { ActList } = await import('./ActList');
	const list = new Vue(ActList);

	list.$mount(mountElement);
}

async function selectSpares() {
	const { ServiceRequestSpareParts } = await import('./ServiceRequestSpareParts');
	const form = new Vue(ServiceRequestSpareParts);

	form.$mount();
	appendToBody(form.$el);
	form.$refs.modal.show();
}

async function changeResponsible() {
	const { default: ModalInstance } = await import('./ServiceRequestChangeResponsibleFormModal');
	const vueModalInstance = new Vue(ModalInstance);

	vueModalInstance.$mount();
	appendToBody(vueModalInstance.$el);
	vueModalInstance.$refs.modal.show();
}

async function changeMaster() {
	const { default: ModalInstance } = await import('./ServiceRequestChangeMasterFormModal');
	const vueModalInstance = new Vue(ModalInstance);

	vueModalInstance.$mount();
	appendToBody(vueModalInstance.$el);
	vueModalInstance.$refs.modal.show();
}

async function checkFunctioning() {
	const { ServiceRequestCheckFunctioning } = await import('./ServiceRequestCheckFunctioning');
	const form = new Vue(ServiceRequestCheckFunctioning);

	form.$mount();
	appendToBody(form.$el);
	form.$refs.modal.show();
}

async function closeRequest() {
	const { ServiceRequestClose } = await import('./ServiceRequestClose');
	const form = new Vue(ServiceRequestClose);

	form.$mount();
	appendToBody(form.$el);
	form.$refs.modal.show();
}

async function hiddenRepair() {
	const mountElement = getElementById('hiddenRepair');

	if (!mountElement)
		return;

	const { ServiceRequestHiddenRepairList } = await import('./ServiceRequestHiddenRepairList');
	const list = new Vue(ServiceRequestHiddenRepairList);

	list.$mount(mountElement);
}

async function addDocuments() {
	const { ServiceRequestAddDocuments } = await import('./ServiceRequestAddDocuments');
	const form = new Vue(ServiceRequestAddDocuments);

	form.$mount();
	appendToBody(form.$el);
	form.$refs.modal.show();
}

async function addOldParts() {
	const { ServiceRequestAddOldParts } = await import('./ServiceRequestAddOldParts');
	const form = new Vue(ServiceRequestAddOldParts);

	form.$mount();
	appendToBody(form.$el);
	form.$refs.modal.show();
}

async function warmUpStore() {
	const { __INITIAL_STATE__: initialState } = BX;

	if (!is(Object, initialState) && !('request' in initialState))
		return;

	const {
		request: {
			canAddRepair,
			canChangeResponsible: isAdministrator,
			canEditHiddenRepair: isServiceAdministrator,
			canEditActs,
			...request
		},
	} = initialState;

	await getAbilityPromise;

	extend('service', [
		{ action: "update", subject: "ServiceRequest" },
		{ action: "update", subject: "ServiceRequest", field: "SERVICE_ID", inverted: !isAdministrator },
		{ action: "create", subject: "Repair", inverted: !canAddRepair },
		{ action: "create", subject: "Act", inverted: !canEditActs},
		{ action: "update", subject: "Act", inverted: !canEditActs},
	])

	if ('device' in request && is(Object, request.device))
		store.device = adoptDevice(request.device);

	if ('iris' in request && is(Object, request.iris))
		store.iris = map(adoptIris, request.iris);

	if ('repairs' in request && is(Array, request.repairs))
		store.repairs = map(adoptRepair, request.repairs);

	if ('hiddenRepair' in request && is(String, request.hiddenRepair))
		store.hiddenRepair = request.hiddenRepair;

	if ('serviceInfo' in request && is(Object, request.serviceInfo)) {
		const { RESPONSIBLE: responsible, SERVICE: service } = request.serviceInfo;

		if (is(Object, service))
			store.service = adoptService(service);

		if (is(Object, responsible))
			store.responsible = adoptResponsible(responsible);
	}

	if ('acts' in request && is(Array, request.acts))
		store.acts = map(adoptAct, request.acts);

	if ('spares' in request && is(Array, request.spares)) {
		const storeSpareParts = new SparePartsCollection();

		for (const part of request.spares) {
			storeSpareParts.set(adoptPart(part));
		}
	}

	if ('actForms' in request && is(Array, request.actForms))
		store.actForms = map(adoptActForm, request.actForms);

	if ('serial' in request && is(String, request.serial))
		store.serialNumber = request.serialNumber;

	if ('isGuarantee' in request && is(Boolean, request.isGuarantee))
		store.isGuarantee = request.isGuarantee;

	console.log(store);
}

document.addEventListener('DOMContentLoaded', async () => {
	// Temporary solution preventing initialization of this mess
	const shouldContinueInit = document.querySelector('#service-request-edit')

	if (!shouldContinueInit) return;

	await warmUpStore();

	// acts

	makeActs();

	// repair

	makeRepairs();

	// spares

	getElementById('selectSpares')?.addEventListener('click', selectSpares);

	// close request

	getElementById('checkFunctioning')?.addEventListener('click', checkFunctioning);

	// close request

	getElementById('closeRequest')?.addEventListener('click', closeRequest);

	// change responsible

	getElementById('changeResponsible')?.addEventListener('click', changeResponsible);

	// change master

	getElementById('changeMaster')?.addEventListener('click', changeMaster);

	// hidden repair

	hiddenRepair()

	// add documents

	getElementById('addDocuments')?.addEventListener('click', addDocuments);

	// add old parts

	getElementById('addOldParts')?.addEventListener('click', addOldParts);
});
